/**
 * Surface 4 — Collector Auto-Guidance Banner (Phase 3 UI).
 * See /app/memory/design_tax_collectors_ui.md §3 Surface 4.
 *
 * SCOPE (locked May 29, 2026 — Source-of-Truth Policy Option B):
 *   This banner renders ONLY for Pennsylvania jurisdictions. Non-PA
 *   jurisdictions stay on the legacy Jurisdiction Directory workflow
 *   with zero workflow changes — caller passes `state !== 'PA'` and
 *   we return null fast.
 *
 * Confidence display rules (LOCKED):
 *   HIGH   → no visual treatment (component returns null silently)
 *   MEDIUM → inline blue info pill (assignment matched the period only)
 *   LOW    → yellow banner + ⚑ Flag for Review button (legacy fallback)
 *   NONE   → red banner + ⚑ Flag for Review button (no config at all)
 *
 * Order completion is ALWAYS permitted regardless of confidence — this
 * banner is informational + flag-only, never a blocker.
 *
 * Flag-button visibility is gated by `can_submit_collector_corrections`
 * permission OR a default allowlist enforced server-side. We optimistically
 * render the button for everyone except known read-only roles; the backend
 * is the source of truth.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Info, Loader2, Plus, ExternalLink, Landmark, Link2, CalendarDays, FileText, MessageSquare, UserRound, Phone, MapPin, Send, Users, Shuffle, MoreHorizontal } from 'lucide-react';
import '../../pages/ccr-premium.css';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  resolveCollectorGuidance,
  submitCorrectionRequest,
  listJurisdictionAssignments,
  updateJurisdictionAssignment,
} from '../../utils/taxCollectorsApi';
import { CollectorGuidanceCompact } from './CollectorGuidanceCompact';
import { holdsAnyRole } from '../../utils/heldRoles';

// Roles whose users typically can't submit correction flags. The backend
// is authoritative — this is only a client-side render hint.
// PA-COLLECTOR-AUTOMATION-001 P0 — mirrors backend `_WRITE_ROLES` for jurisdiction_collector_assignments.
const ASSIGNMENT_WRITE_ROLES = new Set(['admin', 'jurisdiction_manager']);

const NO_FLAG_ROLES = new Set([
  'order_desk', 'accounting', 'it_support', 'it_manager',
  'hr_admin', 'employee', 'qc_specialist',
]);
// RC19 DUAL-ROLE-RBAC-001 — processor roles that, when HELD as an additional role, unlock the flag workflow.
const HELD_FLAG_ROLES = ['processor', 'tax_processor', 'tax_search_processor'];

// CORRECTION-FLOW-CLARITY-001 — processor-facing tiles over the unchanged request_type values.
const INCORRECT_TYPES = new Set(['incorrect_name', 'incorrect_phone', 'incorrect_address']);
const WRONG_TILES = [
  ['wrong', 'Wrong collector assigned', Shuffle, 'wrong_collector_entirely'],
  ['missing', 'Collector missing', Plus, 'missing_collector'],
  ['incorrect_info', 'Incorrect collector information', Info, 'incorrect_name'],
  ['other', 'Other issue', MoreHorizontal, 'other'],
];
function asResolvedLabel(source) {
  if (source === 'assignment')          return 'Assignment';
  if (source === 'legacy_jurisdiction') return 'Legacy Directory';
  return 'None';
}

// Feb 2026 P0 (PA-RESOLVER-LEGACY-FALLBACK-UX) — Human-readable label
// for a tax_category_code so the banner can render "County Tax",
// "Municipal Tax", etc instead of the raw code. Falls back to a
// title-cased version of the code when we don't have an explicit
// alias. Pure function so it's trivially unit-testable.
const CATEGORY_LABEL = {
  county:    'County Tax',
  municipal: 'Municipal Tax',
  school:    'School Tax',
  tax_claim: 'Tax Claim (Delinquent)',
  borough:   'Borough Tax',
  township:  'Township Tax',
  city:      'City Tax',
};
function prettyCategory(code) {
  if (!code) return null;
  if (CATEGORY_LABEL[code]) return CATEGORY_LABEL[code];
  return String(code).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// Classify why an existing assignment was passed over by the resolver
// for the (taxPeriod, desiredCategory, municipality) the banner was rendered for.
// Mirrors `resolve_collector` in services/collector_resolver.py
// (period → effective window → municipality scope → active gate → category);
// any classifier change here MUST stay in sync with the resolver.
// ADMIN-COLLECTOR-COMPACT-001 — window + scope checks added: rows the resolver
// legitimately skipped were previously mislabelled "Eligible" under the warning.
const dateOnly = (v) => (v ? String(v).slice(0, 10) : null);
function classifyExclusion(assignment, taxPeriod, desiredCategory, municipalityJurisdictionId = null, today = dateOnly(new Date().toISOString())) {
  const period = String(assignment?.tax_period || '').toLowerCase();
  const cat = assignment?.tax_category_code || null;
  if (period !== String(taxPeriod || 'current').toLowerCase()) {
    return {
      code: 'period_mismatch',
      label: `Period: ${assignment?.tax_period || 'unknown'}`,
    };
  }
  const from = dateOnly(assignment?.effective_from);
  const to = dateOnly(assignment?.effective_to);
  if (from && today < from) return { code: 'not_yet_effective', label: `Effective from ${from}` };
  if (to && today > to) return { code: 'expired', label: `Expired ${to}` };
  const scope = assignment?.scope_jurisdiction_id || null;
  if (scope && scope !== municipalityJurisdictionId) {
    return {
      code: 'scope_mismatch',
      label: municipalityJurisdictionId ? 'Scoped to another municipality' : 'Municipality-scoped (no municipality on this parcel)',
    };
  }
  if (assignment?.is_active === false) {
    return { code: 'inactive', label: 'Inactive' };
  }
  if (desiredCategory && cat && cat !== desiredCategory) {
    return {
      code: 'category_mismatch',
      label: `Category: ${prettyCategory(cat)}`,
    };
  }
  return { code: 'eligible', label: 'Eligible' };
}

/**
 * Open the Admin → Jurisdictions page in a new tab focused on the given
 * jurisdiction, with the Collectors & Assignments tab pre-selected.
 *
 * Low-risk: this is pure navigation. The directory page reads the
 * `focus` + `tab` query params and auto-opens the edit modal. No DB
 * writes, no API calls.
 */
function openAssignmentsTab(jurisdictionId) {
  if (!jurisdictionId) return;
  const url = `/admin/jurisdictions?focus=${encodeURIComponent(jurisdictionId)}&tab=assignments`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

const CollectorGuidanceBanner = ({
  jurisdictionId,
  jurisdictionState,
  jurisdictionName,
  taxPeriod = 'current',
  taxCategoryCode = null,
  // Feb 2026 (Option C) — Municipality scope hint. When the parcel
  // has a sibling township/borough/city/town/village with its own
  // scoped assignment, pass its jurisdiction_id here so the banner
  // reflects the actual resolved collector for that municipality.
  municipalityJurisdictionId = null,
  orderId = null,
  parcelId = null,
  currentUser = null,
  /** Admin/JM: render the compact verified line in HIGH-confidence cases (P3 default on). */
  showHighConfidencePill = true,
  /** REPORT-ISSUE-REMOVAL-001 — ref the single "Request Correction" entry uses to open the collector flag modal. */
  entryRef = null,
}) => {
  const isPA = useMemo(
    () => (jurisdictionState || '').toUpperCase() === 'PA',
    [jurisdictionState],
  );
  // P3 — roles without assignment-write permission get the compact read-only status.
  const isAssignmentWriter = ASSIGNMENT_WRITE_ROLES.has(currentUser?.role);

  const [state, setState] = useState({
    loading: false,
    guidance: null,   // { resolved, source, confidence, collector, ... }
    error: null,
    // Feb 2026 P0 (PA-RESOLVER-LEGACY-FALLBACK-UX) — full list of
    // existing assignments on this jurisdiction (active + inactive,
    // all periods + categories), joined with the collector record.
    // Loaded lazily ONLY when confidence is LOW or NONE so the
    // happy path stays a single round-trip.
    existingAssignments: null,
    // Per-assignment activation in-flight flag, keyed by assignment id.
    activatingId: null,
  });

  const [flagModal, setFlagModal] = useState({
    open: false,
    submitting: false,
    request_type: 'missing_collector',
    description: '',
    suggested_collector_name: '',
    suggested_collector_phone: '',
    suggested_collector_address: '',
  });
  // ADMIN-COLLECTOR-COMPACT-001 — Admin/JM "Details" disclosure for the existing-assignments list.
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Fetch guidance whenever the jurisdiction context changes
  useEffect(() => {
    if (!isPA || !jurisdictionId) {
      setState({ loading: false, guidance: null, error: null, existingAssignments: null, activatingId: null });
      return;
    }
    let cancelled = false;
    const refKey = `${jurisdictionId}|${taxPeriod}|${taxCategoryCode || ''}|${municipalityJurisdictionId || ''}`;
    setState((s) => ({ ...s, loading: true, error: null }));
    (async () => {
      try {
        const res = await resolveCollectorGuidance([
          {
            ref_id: refKey,
            jurisdiction_id: jurisdictionId,
            tax_period: taxPeriod,
            tax_category_code: taxCategoryCode || null,
            municipality_jurisdiction_id: municipalityJurisdictionId || null,
          },
        ]);
        const g = (res?.guidance || [])[0] || null;
        // Feb 2026 P0 (PA-RESOLVER-LEGACY-FALLBACK-UX) — On legacy
        // fallback (LOW) or NONE, fetch every assignment on this
        // jurisdiction (active + inactive) so the banner can
        // explain why each one was passed over and offer a 1-click
        // activate path when the only thing missing is the active
        // flag. The list endpoint already joins the collector doc.
        let existing = null;
        if (isAssignmentWriter && g && (g.confidence === 'low' || g.confidence === 'none')) {
          try {
            const a = await listJurisdictionAssignments(jurisdictionId, { active_only: false });
            existing = Array.isArray(a?.items) ? a.items : [];
          } catch (_assnErr) {
            // Non-fatal — banner still works without the inline list.
            existing = null;
          }
        }
        if (!cancelled) setState({ loading: false, guidance: g, error: null, existingAssignments: existing, activatingId: null });
      } catch (err) {
        if (!cancelled) {
          setState({
            loading: false,
            guidance: null,
            error: err?.response?.data?.detail || err?.message || 'Failed to resolve',
            existingAssignments: null,
            activatingId: null,
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isPA, jurisdictionId, taxPeriod, taxCategoryCode, municipalityJurisdictionId, isAssignmentWriter]);

  const openFlagModal = useCallback(() => {
    const g = state.guidance;
    const initialType = g?.confidence === 'none' ? 'missing_collector' : 'incorrect_name';
    setFlagModal({
      open: true,
      submitting: false,
      request_type: initialType,
      description: '',
      suggested_collector_name: '',
      suggested_collector_phone: '',
      suggested_collector_address: '',
    });
  }, [state.guidance]);

  // REPORT-ISSUE-REMOVAL-001 — expose the (unchanged) flag workflow to the consolidated Request Correction chooser.
  // RC19 DUAL-ROLE-RBAC-001 — a held processor role (e.g. QC Specialist + Tax Processor) may flag; mirrors backend _DEFAULT_FLAG_ROLES.
  const entryCanFlag = !NO_FLAG_ROLES.has(currentUser?.role) || holdsAnyRole(currentUser, HELD_FLAG_ROLES);
  useEffect(() => {
    if (!entryRef) return undefined;
    const g = state.guidance;
    const available = isPA && !!jurisdictionId && !!g && entryCanFlag;
    entryRef.current = available
      ? { available: true, open: openFlagModal, pendingReview: g.pending_review || null }
      : { available: false, reason: !isPA ? 'Collector corrections apply to PA jurisdictions' : !entryCanFlag ? 'Your role cannot flag collectors' : 'Collector guidance not available yet' };
    return () => { entryRef.current = { available: false }; };
  }, [entryRef, isPA, jurisdictionId, state.guidance, entryCanFlag, openFlagModal]);

  const closeFlagModal = useCallback(() => {
    if (flagModal.submitting) return;
    setFlagModal((s) => ({ ...s, open: false }));
  }, [flagModal.submitting]);

  // Feb 2026 P0 (PA-RESOLVER-LEGACY-FALLBACK-UX) — 1-click activate
  // for an existing-but-inactive assignment that DOES match the
  // requested period + category. Reuses the existing PUT endpoint
  // (`updateJurisdictionAssignment`) so the audit trail still
  // attributes the change to the current user. After a successful
  // activate the banner re-runs `resolveCollectorGuidance` to pick
  // up the new winner.
  const handleActivateAssignment = useCallback(async (assignmentId) => {
    if (!assignmentId) return;
    setState((s) => ({ ...s, activatingId: assignmentId }));
    try {
      await updateJurisdictionAssignment(assignmentId, { is_active: true });
      toast.success('Assignment activated. Re-resolving collector…');
      // Trigger a re-fetch by bumping a synthetic ref.
      const refKey = `${jurisdictionId}|${taxPeriod}|${taxCategoryCode || ''}|${municipalityJurisdictionId || ''}`;
      const res = await resolveCollectorGuidance([
        {
          ref_id: refKey,
          jurisdiction_id: jurisdictionId,
          tax_period: taxPeriod,
          tax_category_code: taxCategoryCode || null,
          municipality_jurisdiction_id: municipalityJurisdictionId || null,
        },
      ]);
      const g = (res?.guidance || [])[0] || null;
      let existing = null;
      if (g && (g.confidence === 'low' || g.confidence === 'none')) {
        try {
          const a = await listJurisdictionAssignments(jurisdictionId, { active_only: false });
          existing = Array.isArray(a?.items) ? a.items : [];
        } catch (_) { existing = null; }
      }
      setState({ loading: false, guidance: g, error: null, existingAssignments: existing, activatingId: null });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Failed to activate assignment.');
      setState((s) => ({ ...s, activatingId: null }));
    }
  }, [jurisdictionId, taxPeriod, taxCategoryCode, municipalityJurisdictionId]);

  const handleFlagSubmit = useCallback(async () => {
    const trimmed = (flagModal.description || '').trim();
    if (trimmed.length < 10) {
      toast.error('Description must be at least 10 characters.');
      return;
    }
    const g = state.guidance;
    setFlagModal((s) => ({ ...s, submitting: true }));
    try {
      const body = {
        jurisdiction_id:    jurisdictionId,
        tax_period:         taxPeriod,
        tax_category_code:  taxCategoryCode || null,
        order_id:           orderId || null,
        parcel_id:          parcelId || null,
        resolved_confidence:   g?.confidence || 'none',
        resolved_collector_id: g?.collector?.id || null,
        resolved_source:       g?.source || 'none',
        request_type:          flagModal.request_type,
        description:           trimmed,
        suggested_collector_name:    flagModal.suggested_collector_name.trim() || null,
        suggested_collector_phone:   flagModal.suggested_collector_phone.trim() || null,
        suggested_collector_address: flagModal.suggested_collector_address.trim() || null,
      };
      const res = await submitCorrectionRequest(body);
      if (res?.__merged_into_existing) {
        toast.info(
          `Acknowledged. This jurisdiction was already flagged by ${res.submitted_by_name || 'another user'}; we'll bump the impact count.`,
        );
      } else {
        toast.success('Correction request submitted. The Jurisdiction Manager will review it.');
      }
      setFlagModal((s) => ({ ...s, open: false, submitting: false }));
      // REPORT-ISSUE-REMOVAL-001 — reflect the new pending review locally (no refetch) so the compact line + chooser update.
      const reviewId = res?.id || res?.existing_id || res?.request?.id || null;
      if (reviewId) setState((s) => (s.guidance ? { ...s, guidance: { ...s.guidance, pending_review: { id: reviewId, request_type: body.request_type, status: 'pending' } } } : s));
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to submit';
      toast.error(detail);
      setFlagModal((s) => ({ ...s, submitting: false }));
    }
  }, [
    flagModal.description, flagModal.request_type,
    flagModal.suggested_collector_name, flagModal.suggested_collector_phone,
    flagModal.suggested_collector_address,
    state.guidance, jurisdictionId, taxPeriod, taxCategoryCode, orderId, parcelId,
  ]);

  // ── EARLY RETURNS ─────────────────────────────────────────────
  if (!isPA) return null;             // non-PA → zero visual change
  if (!jurisdictionId) return null;   // not saved yet
  if (state.loading) {
    return (
      <div
        className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500"
        data-testid="collector-guidance-loading"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Resolving collector for this jurisdiction…
      </div>
    );
  }
  if (state.error) {
    return (
      <div
        className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600"
        data-testid="collector-guidance-error"
      >
        <Info className="h-3.5 w-3.5" />
        Collector auto-guidance unavailable ({state.error}).
      </div>
    );
  }
  const g = state.guidance;
  if (!g) return null;

  const confidence = g.confidence || 'none';
  // Payee precedence mirrors TaxCollectorPicker / onLinkMasterData: master payable name → collector display name.
  const payee = g.collector?.make_checks_payable_to || g.collector?.display_name || null;

  // ── P3 — compact read-only status for processors / team leaders ──
  if (!isAssignmentWriter) {
    return (
      <>
        <CollectorGuidanceCompact
          confidence={confidence}
          ambiguous={!!g.ambiguous}
          pendingReview={g.pending_review || null}
          collectorName={g.collector?.display_name || null}
          payee={payee}
          jurisdictionName={jurisdictionName}
          categoryLabel={prettyCategory(taxCategoryCode)}
          canReport={false}
          onReport={openFlagModal}
        />
        {renderFlagModal()}
      </>
    );
  }

  // ── Admin / Jurisdiction Manager — verified states share the compact line ──
  if (confidence === 'high' || confidence === 'medium') {
    if (confidence === 'high' && !showHighConfidencePill) return null;
    return (
      <>
      <CollectorGuidanceCompact
        confidence={confidence}
        ambiguous={false}
        pendingReview={g.pending_review || null}
        collectorName={g.collector?.display_name || null}
        payee={payee}
        jurisdictionName={jurisdictionName}
        canReport={false}
      />
      {renderFlagModal()}
      </>
    );
  }

  // ── AMBIGUOUS (Jul 2026 — AMBIGUOUS-ASSIGNMENT-GUARD) ─────────
  // ≥2 equally ranked assignments matched — assignments DO exist,
  // so the "No active assignment found / legacy fallback" copy and
  // the Create Assignment CTA are wrong here. Simplified processor
  // copy; the full assignment breakdown stays available behind
  // "Review Assignments" (jurisdiction's Collectors & Assignments tab).
  if (g.ambiguous) {
    const categoryLabel = prettyCategory(taxCategoryCode);
    const tiedNames = [
      g.collector?.display_name,
      ...(Array.isArray(g.tied_collectors) ? g.tied_collectors.map((t) => t.collector_name) : []),
    ].filter(Boolean);
    const matchSentence = tiedNames.length >= 2
      ? `${tiedNames.slice(0, -1).join(', ')} and ${tiedNames[tiedNames.length - 1]} ${tiedNames.length === 2 ? 'both' : 'all'} match.`
      : null;
    return (
      <>
        <div
          className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
          data-testid="collector-guidance-ambiguous"
          role="status"
          aria-live="polite"
          aria-label="Collector guidance: ambiguous"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
          <div className="flex-1 min-w-0">
            <div className="font-medium" data-testid="collector-guidance-ambiguous-headline">
              Ambiguous {categoryLabel ? `${categoryLabel} ` : ''}collector — municipality required
            </div>
            {matchSentence && (
              <div className="mt-0.5 text-xs text-amber-800/90" data-testid="collector-guidance-ambiguous-matches">
                {matchSentence}
              </div>
            )}
            <div className="mt-0.5 text-xs font-medium text-amber-900" data-testid="collector-guidance-ambiguous-action">
              Action: Add the property municipality or select the correct collector.
            </div>
            <div className="mt-1 text-[11px] text-amber-800/80" data-testid="collector-guidance-ambiguous-temp">
              Temporary selection made from multiple matching assignments — processor review
              required. Current temporary selection:{' '}
              <strong>{g.collector?.display_name || '—'}</strong>.
            </div>
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="border-amber-300 bg-white text-amber-800 hover:bg-amber-100"
              onClick={() => openAssignmentsTab(jurisdictionId)}
              data-testid="collector-guidance-review-assignments-btn"
              title="Open this jurisdiction's Collectors & Assignments tab to review or scope the matching assignments"
            >
              <ExternalLink className="mr-1 h-3.5 w-3.5" /> Review Assignments
            </Button>
          </div>
        </div>
        {renderFlagModal()}
      </>
    );
  }

  // ── Admin / JM — LOW / NONE (ADMIN-COLLECTOR-COMPACT-001) ────────
  // Same compact line the Processor view renders (identical headline /
  // tone rules via CollectorGuidanceCompact). The former full-width amber
  // "No active <Category> assignment found" banner is replaced by a quiet
  // "Details" disclosure that lists the existing assignments with the
  // REAL reason the resolver skipped each one (period / window / scope /
  // inactive / category). Assignment creation lives in the Jurisdiction
  // Directory; "Manage in Directory" is navigation only.
  const categoryLabel = prettyCategory(taxCategoryCode);
  const existing = Array.isArray(state.existingAssignments) ? state.existingAssignments : [];
  const isLow = confidence === 'low';
  const headline = categoryLabel
    ? `No active ${categoryLabel} assignment found for ${jurisdictionName || 'this jurisdiction'}.`
    : `Using legacy directory data for ${jurisdictionName || 'this jurisdiction'}.`;
  const linkCls = 'inline-flex items-center gap-1 text-[11px] underline-offset-2 opacity-80 hover:underline hover:opacity-100';
  return (
    <>
      <div
        data-testid={isLow ? 'collector-guidance-low' : 'collector-guidance-none'}
        aria-label={`Collector confidence: ${confidence}`}
      >
        <CollectorGuidanceCompact
          confidence={confidence}
          ambiguous={false}
          pendingReview={g.pending_review || null}
          collectorName={g.collector?.display_name || null}
          payee={payee}
          jurisdictionName={jurisdictionName}
          categoryLabel={categoryLabel}
          canReport={false}
          actions={(
            <>
              {isLow && existing.length > 0 && (
                <button
                  type="button"
                  className={linkCls}
                  aria-expanded={detailsOpen}
                  onClick={() => setDetailsOpen((o) => !o)}
                  data-testid="collector-guidance-details-toggle"
                >
                  {detailsOpen ? 'Hide details' : `Details (${existing.length})`}
                </button>
              )}
              <button
                type="button"
                className={linkCls}
                onClick={() => openAssignmentsTab(jurisdictionId)}
                data-testid="collector-guidance-manage-directory-link"
                title="Open this jurisdiction's Collectors & Assignments tab in the Jurisdiction Directory (new tab)"
              >
                <ExternalLink className="h-3 w-3" /> Manage in Directory
              </button>
            </>
          )}
        >
          {isLow && detailsOpen && (
            <div
              className="mt-1.5 rounded border border-slate-200 bg-white/70 px-2 py-1.5"
              data-testid="collector-guidance-existing-assignments"
            >
              <div className="text-[11px] text-slate-600" data-testid="collector-guidance-low-headline">
                {headline} Falling back to legacy directory data.
              </div>
              <div className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Existing assignments on this jurisdiction ({existing.length})
              </div>
              <ul className="mt-1 space-y-1" data-testid="collector-guidance-existing-list">
                {existing.map((a) => {
                  const cls = classifyExclusion(a, taxPeriod, taxCategoryCode, municipalityJurisdictionId);
                  const collectorName = a?.collector?.display_name || a?.collector_id || '—';
                  const isActivatable =
                    cls.code === 'inactive'
                    && String(a?.tax_period || '').toLowerCase() === String(taxPeriod || 'current').toLowerCase()
                    && (!taxCategoryCode
                        || (a?.tax_category_code || null) === taxCategoryCode
                        || a?.tax_category_code == null);
                  const pillClass =
                    cls.code === 'eligible' ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                    : cls.code === 'inactive' ? 'bg-slate-100 text-slate-700 border-slate-200'
                    : 'bg-amber-100 text-amber-800 border-amber-200';
                  return (
                    <li
                      key={a.id}
                      className="flex flex-wrap items-center gap-2 text-xs"
                      data-testid={`collector-guidance-existing-row-${a.id}`}
                    >
                      <span className="font-medium text-slate-800">{collectorName}</span>
                      <span className="text-slate-500">
                        {prettyCategory(a?.tax_category_code) || 'All categories'}
                        {' · '}
                        {a?.tax_period === 'delinquent' ? 'Delinquent' : 'Current'}
                      </span>
                      <span
                        className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${pillClass}`}
                        data-testid={`collector-guidance-existing-status-${a.id}`}
                      >
                        {cls.label}
                      </span>
                      {isActivatable && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="ml-auto h-6 border-emerald-300 bg-white px-2 py-0 text-[11px] text-emerald-800 hover:bg-emerald-50"
                          disabled={state.activatingId === a.id}
                          onClick={() => handleActivateAssignment(a.id)}
                          data-testid={`collector-guidance-activate-btn-${a.id}`}
                          title="Re-activate this assignment and re-resolve the collector"
                        >
                          {state.activatingId === a.id ? (
                            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                          ) : null}
                          Activate
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
              <div className="mt-1.5 text-[11px] text-slate-500">
                Need a different setup? Use <strong>Manage in Directory</strong> to add the
                correct {categoryLabel || 'category'} payee — even if the collector
                entity itself is a township / borough, you can still assign it under{' '}
                {categoryLabel || 'this category'} when that office is the real-world
                recipient.
              </div>
            </div>
          )}
        </CollectorGuidanceCompact>
      </div>
      {renderFlagModal()}
    </>
  );

  // ── Inline Flag dialog ────────────────────────────────────────
  function renderFlagModal() {
    // COLLECTOR-FLAG-PREMIUM-UI-001 — presentation only; state, validation, submit body and testids unchanged.
    const g = state.guidance;
    const descLen = (flagModal.description || '').length;
    const canSubmit = !flagModal.submitting && flagModal.description.trim().length >= 10;
    const summary = [
      ['Jurisdiction', jurisdictionName || jurisdictionId, Landmark, 'flag-summary-jurisdiction'],
      ['Currently resolved', `${g?.collector?.display_name || '—'} (${asResolvedLabel(g?.source)})`, Link2, 'flag-summary-resolved'],
      ['Period', `${taxPeriod === 'current' ? 'Current' : 'Delinquent'}${taxCategoryCode ? ` · ${prettyCategory(taxCategoryCode)}` : ''}`, CalendarDays, 'flag-summary-period'],
      ...(orderId ? [['Order', orderId, FileText, 'flag-summary-order']] : []),
    ];
    return (
      <Dialog open={flagModal.open} onOpenChange={(open) => { if (!open) closeFlagModal(); }}>
        <DialogContent className="ccr-modal cfp-modal cfp-amber" data-testid="flag-review-dialog">
          <DialogHeader className="ccr-head space-y-0 text-left">
            <span className="ccr-head-icon cfp-head-icon"><Users /></span>
            <div className="min-w-0">
              <DialogTitle className="ccr-title">Report Collector Problem</DialogTitle>
              <DialogDescription className="cfp-sub">Report an issue with the collector or collector assignment for this jurisdiction. The Jurisdiction Manager will review and update the collector records.</DialogDescription>
            </div>
          </DialogHeader>
          <div className="ccr-body cfp-body">
            <div className="cfp-summary" data-testid="flag-context">
              {summary.map(([label, value, Icon, tid]) => (
                <div key={label} className="cfp-sum-item" data-testid={tid}>
                  <span className="cfp-sum-icon"><Icon /></span>
                  <div className="min-w-0"><div className="ccr-tile-label">{label}</div><div className="cfp-sum-value" title={String(value)}>{value}</div></div>
                </div>
              ))}
            </div>

            {/* CORRECTION-FLOW-CLARITY-001 — task-based "What's wrong?" tiles; values map 1:1 onto the existing request_type set */}
            <div className="cfp-field">
              <Label className="cfp-label">What&apos;s wrong? <span className="text-rose-500">*</span></Label>
              <div className="cfp-wrong" role="group" aria-label="What's wrong" data-testid="flag-type-select">
                {WRONG_TILES.map(([key, label, Icon, type]) => {
                  const active = key === 'incorrect_info' ? INCORRECT_TYPES.has(flagModal.request_type) : flagModal.request_type === type;
                  return (
                    <button key={key} type="button" aria-pressed={active} className="cfp-wrong-tile" onClick={() => setFlagModal((st) => ({ ...st, request_type: key === 'incorrect_info' ? (INCORRECT_TYPES.has(st.request_type) ? st.request_type : 'incorrect_name') : type }))} data-testid={`flag-type-opt-${key === 'incorrect_info' ? 'incorrect_info' : type}`}>
                      <Icon /><span>{label}</span>
                    </button>
                  );
                })}
              </div>
              {INCORRECT_TYPES.has(flagModal.request_type) && (
                <div className="cfp-detail" data-testid="flag-type-detail">
                  <span className="cfp-detail-label">Which detail?</span>
                  {[['incorrect_name', 'Name'], ['incorrect_phone', 'Phone'], ['incorrect_address', 'Address']].map(([v, l]) => (
                    <button key={v} type="button" aria-pressed={flagModal.request_type === v} className="cfp-detail-chip" onClick={() => setFlagModal((st) => ({ ...st, request_type: v }))} data-testid={`flag-type-opt-${v}`}>{l}</button>
                  ))}
                </div>
              )}
            </div>

            <div className="cfp-field">
              <Label htmlFor="ccr-desc" className="cfp-label">Describe the problem <span className="text-rose-500">*</span> <span className="cfp-hint">(min 10 chars)</span></Label>
              <div className="cfp-input-wrap is-area">
                <span className="cfp-input-icon"><MessageSquare /></span>
                <Textarea id="ccr-desc" rows={3} maxLength={500} value={flagModal.description} onChange={(e) => setFlagModal((s) => ({ ...s, description: e.target.value }))}
                  placeholder="Please describe the issue in detail (e.g. wrong phone number, incorrect address, wrong collector assigned)." className="cfp-area" data-testid="flag-description-input" />
                <span className={`cfp-counter ${descLen > 0 && descLen < 10 ? 'is-low' : ''}`} data-testid="flag-description-counter">{descLen}/500</span>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="cfp-field">
                <Label htmlFor="ccr-name" className="cfp-label">Suggested collector name</Label>
                <div className="cfp-input-wrap"><span className="cfp-input-icon"><UserRound /></span>
                  <Input id="ccr-name" value={flagModal.suggested_collector_name} onChange={(e) => setFlagModal((s) => ({ ...s, suggested_collector_name: e.target.value }))} placeholder="Optional" className="cfp-input" data-testid="flag-suggest-name-input" />
                </div>
              </div>
              <div className="cfp-field">
                <Label htmlFor="ccr-phone" className="cfp-label">Suggested phone</Label>
                <div className="cfp-input-wrap"><span className="cfp-input-icon"><Phone /></span>
                  <Input id="ccr-phone" value={flagModal.suggested_collector_phone} onChange={(e) => setFlagModal((s) => ({ ...s, suggested_collector_phone: e.target.value }))} placeholder="Optional" className="cfp-input" data-testid="flag-suggest-phone-input" />
                </div>
              </div>
            </div>
            <div className="cfp-field">
              <Label htmlFor="ccr-addr" className="cfp-label">Suggested address</Label>
              <div className="cfp-input-wrap is-area"><span className="cfp-input-icon"><MapPin /></span>
                <Textarea id="ccr-addr" rows={2} value={flagModal.suggested_collector_address} onChange={(e) => setFlagModal((s) => ({ ...s, suggested_collector_address: e.target.value }))} placeholder="Optional" className="cfp-area" data-testid="flag-suggest-address-input" />
              </div>
            </div>

            <div className="cfp-footer">
              <div className="cfp-note"><Info /><span>Reviewed by the Jurisdiction Manager — you'll be notified once the collector information is updated.</span></div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={closeFlagModal} disabled={flagModal.submitting} className="cfp-cancel" data-testid="flag-cancel-btn">Cancel</Button>
                <Button onClick={handleFlagSubmit} disabled={!canSubmit} className="ccr-cta cfp-cta" data-testid="flag-review-submit-button">
                  {flagModal.submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…</> : <><Send className="mr-2 h-4 w-4" /> Submit Flag</>}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
};

export default CollectorGuidanceBanner;
